import React from 'react';
import { FOUP } from '../FOUP/FOUP';
import { AmhsLabel, StatusIndicator } from '../shared';
import { AMHS_PALETTE, resolveFoupKindColor, type EquipmentStatus } from '../types';

export type ConveyorProps = {
  width?: number;
  height?: number;
  depth?: number;
  foupPresent?: boolean;
  status?: EquipmentStatus;
  showLabel?: boolean;
  label?: string;
  opacity?: number;
};

/**
 * Conveyor-style AMHS equipment.
 * Length along local +X; rollers along length; origin bottom-center.
 */
export const Conveyor: React.FC<ConveyorProps> = ({
  width = 3.5,
  height = 1.1,
  depth = 1.2,
  foupPresent = false,
  status = 'RUNNING',
  showLabel = false,
  label = 'CVY',
  opacity = 1,
}) => {
  const rollerCount = Math.max(4, Math.round(width / 0.35));
  const deckY = height * 0.55;

  return (
    <group name="Conveyor">
      {/* Legs */}
      {(
        [
          [-width * 0.42, -depth * 0.35],
          [width * 0.42, -depth * 0.35],
          [-width * 0.42, depth * 0.35],
          [width * 0.42, depth * 0.35],
        ] as [number, number][]
      ).map(([x, z], i) => (
        <mesh key={i} position={[x, deckY * 0.45, z]} castShadow>
          <boxGeometry args={[0.08, deckY * 0.9, 0.08]} />
          <meshStandardMaterial color={AMHS_PALETTE.metalDark} metalness={0.6} roughness={0.35} />
        </mesh>
      ))}

      {/* Frame rails */}
      <mesh position={[0, deckY, -depth * 0.42]} castShadow>
        <boxGeometry args={[width, 0.08, 0.06]} />
        <meshStandardMaterial color={AMHS_PALETTE.metal} metalness={0.65} roughness={0.3} transparent={opacity < 1} opacity={opacity} />
      </mesh>
      <mesh position={[0, deckY, depth * 0.42]} castShadow>
        <boxGeometry args={[width, 0.08, 0.06]} />
        <meshStandardMaterial color={AMHS_PALETTE.metal} metalness={0.65} roughness={0.3} transparent={opacity < 1} opacity={opacity} />
      </mesh>

      {/* Side guides */}
      <mesh position={[0, deckY + 0.12, -depth * 0.45]}>
        <boxGeometry args={[width * 0.98, 0.16, 0.04]} />
        <meshStandardMaterial color={AMHS_PALETTE.amhsBlueBright} metalness={0.35} roughness={0.38} />
      </mesh>
      <mesh position={[0, deckY + 0.12, depth * 0.45]}>
        <boxGeometry args={[width * 0.98, 0.16, 0.04]} />
        <meshStandardMaterial color={AMHS_PALETTE.amhsBlueBright} metalness={0.35} roughness={0.38} />
      </mesh>

      {/* Rollers */}
      {Array.from({ length: rollerCount }, (_, i) => {
        const t = (i + 0.5) / rollerCount;
        const x = -width / 2 + t * width;
        return (
          <mesh key={`roller-${i}`} position={[x, deckY, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
            <cylinderGeometry args={[0.05, 0.05, depth * 0.72, 12]} />
            <meshStandardMaterial
              color={AMHS_PALETTE.metalLight}
              metalness={0.75}
              roughness={0.25}
              transparent={opacity < 1}
              opacity={opacity}
            />
          </mesh>
        );
      })}

      {/* Carrier platform */}
      <mesh position={[0, deckY + 0.07, 0]} castShadow>
        <boxGeometry args={[width * 0.35, 0.04, depth * 0.55]} />
        <meshStandardMaterial color={AMHS_PALETTE.tray} metalness={0.45} roughness={0.4} />
      </mesh>

      {/* Drive housing */}
      <mesh position={[width * 0.42, height * 0.35, 0]} castShadow>
        <boxGeometry args={[width * 0.14, height * 0.55, depth * 0.55]} />
        <meshStandardMaterial
          color={AMHS_PALETTE.body}
          metalness={0.25}
          roughness={0.42}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {foupPresent && (
        <FOUP
          scale={Math.min(depth, 1.1) * 0.85}
          color={resolveFoupKindColor('raw')}
          position={[0, deckY + 0.1, 0]}
          opacity={opacity}
        />
      )}

      <StatusIndicator status={status} position={[width * 0.42, height * 0.75, depth * 0.2]} size={0.05} />
      <AmhsLabel text={label} y={height + 0.15} visible={showLabel} />
    </group>
  );
};

export default Conveyor;
